/**
 * BB-CORE.js - Bundle Builder Core Module
 * 
 * Foundation module providing:
 * - Instance management (supports multiple bundle builders per page)
 * - Shared state and configuration
 * - DOM element caching
 * - Event bus for cross-module communication
 * - Utility functions (money formatting, session IDs, etc.)
 * 
 * All other BB modules depend on this one.
 * 
 * @version 1.0.0
 * @requires None (this is the foundation)
 */

window.BB = window.BB || {};

window.BB.Core = (function() {
  'use strict';
  
  // ========================================================================
  // INSTANCE MANAGEMENT
  // Store multiple bundle builder instances (keyed by sectionId)
  // ========================================================================
  var instances = new Map();
  
  /**
   * Create a new bundle builder instance
   * @param {HTMLElement} container - The .bundle-builder element
   * @returns {Object} Instance object with config, state, els, event bus
   */
  function createInstance(container) {
    var sectionId = container.dataset.sectionId;
    
    // ========================================================================
    // CONFIGURATION (from data attributes)
    // ========================================================================
    var config = {
      sectionId: sectionId,
      timerDuration: parseInt(container.dataset.timerDuration) || 600,
      timerUrgent: parseInt(container.dataset.timerUrgent) || 120,
      queueEnabled: container.dataset.queueEnabled === 'true',
      queueStart: parseInt(container.dataset.queueStart) || 10,
      tiers: {
        2: parseInt(container.dataset.tier2) || 20,
        3: parseInt(container.dataset.tier3) || 30,
        4: parseInt(container.dataset.tier4) || 40,
        5: parseInt(container.dataset.tier5) || 50
      },
      currency: container.dataset.currency || 'GBP',
      moneyFormat: container.dataset.moneyFormat || '£{{amount}}'
    };
    
    // ========================================================================
    // STATE (central state object)
    // ========================================================================
    var state = {
      currentStep: 1,
      totalSteps: container.querySelectorAll('.bb-step-container').length,
      selectedProducts: [],   // {variantId, productId, price, comparePrice, title, handle, type}
      selectedUpgrades: [],   // Digital upgrades
      selectedAddons: [],     // Physical add-ons
      selectedVault: [],      // Vault items
      discountTier: 0,        // Current discount tier (0-50)
      timerRemaining: config.timerDuration,
      timerInterval: null,
      bundleSessionId: generateSessionId(),
      upgradeMode: false,     // True when in upgrade selection mode
      totalSavings: 0         // Cached for fly-off animation
    };
    
    // ========================================================================
    // DOM ELEMENTS (cached references)
    // ========================================================================
    var els = {
      container: container,
      queueScreen: container.querySelector('.bb-queue-screen'),
      queueCount: container.querySelector('.bb-queue-count'),
      header: container.querySelector('.bb-header'),
      main: container.querySelector('.bb-main'),
      footer: container.querySelector('.bb-sticky-footer'),
      timer: container.querySelector('.bb-timer-display'),
      timerValue: container.querySelector('.bb-timer-value'),
      tierFill: container.querySelector('.bb-tier-bar-fill-wrapper'),
      tierStatus: container.querySelector('.bb-tier-status'),
      tierNext: container.querySelector('.bb-tier-next'),
      tierPercentDisplay: container.querySelector('.bb-tier-percent-display'),
      tierPercentValue: container.querySelector('.bb-tier-percent-value'),
      tierPercentSuffix: container.querySelector('.bb-tier-percent-suffix'),
      tierPercentShadow: container.querySelector('.bb-tier-percent-shadow'),
      tierSavingsDisplay: container.querySelector('.bb-tier-savings-display'),
      tierSavingsValue: container.querySelector('.bb-tier-savings-value'),
      tierSavingsPrefix: container.querySelector('.bb-tier-savings-prefix'),
      tierSavingsShadow: container.querySelector('.bb-tier-savings-shadow'),
      tierItemsCount: container.querySelector('.bb-tier-items-count'),
      navPrices: container.querySelectorAll('.bb-nav-price'),
      navSavings: container.querySelectorAll('.bb-nav-savings'),
      navBtns: container.querySelectorAll('.bb-nav-btn-next'),
      stepContainers: container.querySelectorAll('.bb-step-container'),
      stepNavItems: container.querySelectorAll('.bb-step-item')
    };
    
    // ========================================================================
    // EVENT BUS (for decoupled cross-module communication)
    // ========================================================================
    var eventCallbacks = {};
    
    /**
     * Subscribe to an event
     * @param {string} event - Event name (e.g., 'card:selected', 'tier:changed')
     * @param {Function} callback - Handler function
     */
    function on(event, callback) {
      if (!eventCallbacks[event]) {
        eventCallbacks[event] = [];
      }
      eventCallbacks[event].push(callback);
    }
    
    /**
     * Unsubscribe from an event
     * @param {string} event - Event name
     * @param {Function} callback - Handler to remove
     */
    function off(event, callback) {
      if (!eventCallbacks[event]) return;
      var index = eventCallbacks[event].indexOf(callback);
      if (index > -1) {
        eventCallbacks[event].splice(index, 1);
      }
    }
    
    /**
     * Emit an event to all subscribers
     * @param {string} event - Event name
     * @param {*} data - Data to pass to handlers
     */
    function emit(event, data) {
      if (!eventCallbacks[event]) return;
      eventCallbacks[event].forEach(function(callback) {
        try {
          callback(data);
        } catch (e) {
          console.error('[BB.Core] Event handler error for "' + event + '":', e);
        }
      });
    }
    
    // ========================================================================
    // INSTANCE API
    // ========================================================================
    var instance = {
      sectionId: sectionId,
      config: config,
      state: state,
      els: els,
      container: container,
      
      // Event bus
      on: on,
      off: off,
      emit: emit,
      
      // Module references (populated by other modules during init)
      modules: {}
    };
    
    return instance;
  }
  
  // ========================================================================
  // UTILITY FUNCTIONS
  // ========================================================================
  
  /**
   * Generate a unique session ID for bundle tracking
   * @returns {string} Session ID
   */
  function generateSessionId() {
    return 'bb_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
  
  /**
   * Convert cents to formatted amount (no symbol)
   * @param {number} cents - Price in cents
   * @returns {string} Formatted amount
   */
  function formatMoney(cents) {
    return (cents / 100).toFixed(2);
  }
  
  /**
   * Convert cents to formatted amount with currency symbol
   * @param {number} cents - Price in cents
   * @param {Object} config - Instance config with moneyFormat
   * @returns {string} Formatted amount with symbol
   */
  function formatMoneyWithSymbol(cents, config) {
    var amount = formatMoney(cents);
    var format = config ? config.moneyFormat : '£{{amount}}';
    var formatted = format.replace('{{amount}}', amount);
    formatted = formatted.replace('{{amount_no_decimals}}', Math.round(cents / 100));
    formatted = formatted.replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    return formatted;
  }
  
  /**
   * Format decimal price (already in dollars/pounds)
   * @param {number} decimal - Price in decimal format
   * @returns {string} Formatted amount
   */
  function formatMoneyFromDecimal(decimal) {
    return decimal.toFixed(2);
  }
  
  /**
   * Debounce function calls
   * @param {Function} fn - Function to debounce
   * @param {number} delay - Delay in ms
   * @returns {Function} Debounced function
   */
  function debounce(fn, delay) {
    var timeoutId;
    return function() {
      var context = this;
      var args = arguments;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function() {
        fn.apply(context, args);
      }, delay);
    };
  }
  
  /**
   * Throttle function calls
   * @param {Function} fn - Function to throttle
   * @param {number} limit - Minimum time between calls in ms
   * @returns {Function} Throttled function
   */
  function throttle(fn, limit) {
    var lastCall = 0;
    return function() {
      var now = Date.now();
      if (now - lastCall >= limit) {
        lastCall = now;
        fn.apply(this, arguments);
      }
    };
  }
  
  /**
   * Check if device supports hover (not touch-only)
   * @returns {boolean}
   */
  function supportsHover() {
    return window.matchMedia('(hover: hover)').matches;
  }
  
  /**
   * Check if device is touch-primary
   * @returns {boolean}
   */
  function isTouchDevice() {
    return window.matchMedia('(hover: none)').matches;
  }
  
  // ========================================================================
  // PUBLIC API
  // ========================================================================
  
  /**
   * Initialize a bundle builder instance
   * @param {HTMLElement} container - The .bundle-builder element
   * @returns {Object} Instance
   */
  function init(container) {
    var instance = createInstance(container);
    instances.set(instance.sectionId, instance);
    console.log('[BB.Core] Initialized instance:', instance.sectionId);
    return instance;
  }
  
  /**
   * Get an existing instance by section ID
   * @param {string} sectionId
   * @returns {Object|undefined} Instance or undefined
   */
  function getInstance(sectionId) {
    return instances.get(sectionId);
  }
  
  /**
   * Get all instances
   * @returns {Map} All instances
   */
  function getAllInstances() {
    return instances;
  }
  
  /**
   * Get instance from a child element
   * @param {HTMLElement} element - Any element inside a bundle builder
   * @returns {Object|undefined} Instance or undefined
   */
  function getInstanceFromElement(element) {
    var container = element.closest('.bundle-builder');
    if (!container) return undefined;
    var sectionId = container.dataset.sectionId;
    return instances.get(sectionId);
  }
  
  // ========================================================================
  // EXPOSE MODULE
  // ========================================================================
  return {
    // Instance management
    init: init,
    getInstance: getInstance,
    getAllInstances: getAllInstances,
    getInstanceFromElement: getInstanceFromElement,
    
    // Utilities
    generateSessionId: generateSessionId,
    formatMoney: formatMoney,
    formatMoneyWithSymbol: formatMoneyWithSymbol,
    formatMoneyFromDecimal: formatMoneyFromDecimal,
    debounce: debounce,
    throttle: throttle,
    supportsHover: supportsHover,
    isTouchDevice: isTouchDevice,
    
    // Version
    version: '1.0.0'
  };
})();

// Log that core is loaded
console.log('[BB.Core] Module loaded v' + window.BB.Core.version);

